<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import { computedAsync } from '@vueuse/core';
import { isEqual } from 'lodash';

import type { RemoveHotelSelectionFiltersOrigin } from '@apps/main/new-desktop/core/models';
import {
  CoreEventSource,
  HotelDetailsEventSource,
} from '@apps/main/new-desktop/core/models';

import type { AppMultiSelectOption } from '@libs/ui';
import { AppFirstLevelPanel } from '@libs/ui';
import { isExperimentAsync, isFeatureToggleActive } from '@libs/feature-toggle';
import { trackHotelDetailsViewItemForECommerce } from '@libs/google-analytics-events';
import type { BoardBasisCode } from '@libs/trip-planner-interfaces';

import { useStatsigEventsStore } from '../../core/stores';
import FirstDayTripPriceRibbon from '../../core/components/FirstDayTripPriceRibbon.vue';
import { DynamicFullScreenMap, MapType } from '../maps';
import { usePreviouslyUserSessionStore } from '../../core/stores/previouslyUserSession/previouslyUserSessionStore';
import type { HotelFilterMealsUpdate } from '../hotelFilters';
import { useMapStore } from '../../core/stores/mapStore/mapStore';
import { HotelMealFiltersEmptyState } from '../hotelFilters';

import HotelDetailsDescriptionSection from './components/description/HotelDetailsDescriptionSection.vue';
import HotelDetailsGallery from './components/HotelDetailsGallery.vue';
import HotelDetailsGallerySection from './components/HotelDetailsGallerySection.vue';
import HotelDetailsModals from './components/HotelDetailsModals.vue';
import HotelDetailsStaysSection from './components/stays/HotelDetailsStaysSection.vue';
import DestinationInformationSection from './components/DestinationInformationSection.vue';
import GridLayoutHotelDetailsStaysSection from './components/stays/gridLayout/GridLayoutHotelDetailsStaysSection.vue';
import type {
  HotelStaticData,
  HotelDynamicData,
  SelectStayEventPayload,
  StayId,
  ToggleHeartEventPayload,
} from './models';
import { HotelDetailsModalType, HotelDetailsGalleryType } from './models';
import {
  useHotelDetailsGalleryStore,
  useHotelDetailsMapStore,
  useHotelDetailsModalsStore,
  useHotelDetailsPanelStore,
} from './stores';
import ListRoomLoader from './components/loaders/ListRoomLoader.vue';
import HotelDetailsGallerySectionLoader from './components/loaders/HotelDetailsGallerySectionLoader.vue';
import HotelDetailsDescriptionSectionLoader from './components/loaders/HotelDetailsDescriptionSectionLoader.vue';
import HotelStayFilters from './components/stays/HotelStayFilters.vue';

interface HotelDetailsPanelProps {
  hotelStaticData?: HotelStaticData;
  hotelDynamicData?: HotelDynamicData;
  hotelMealFilterOptions?: AppMultiSelectOption<BoardBasisCode>[];
  source: HotelDetailsEventSource;
}

const props = defineProps<HotelDetailsPanelProps>();

interface HotelDetailsPanelEmits {
  (eventName: 'close'): void;
  (eventName: 'stay-selected', eventPayload: SelectStayEventPayload): void;
  (eventName: 'toggle-heart', eventPayload: ToggleHeartEventPayload): void;
  (eventName: 'suggest-another-day'): void;
  (
    eventName: 'meal-option-selected',
    context: {
      filter: HotelFilterMealsUpdate;
      source: RemoveHotelSelectionFiltersOrigin;
    }
  ): void;
}

const emit = defineEmits<HotelDetailsPanelEmits>();

const hotelDetailsPanelStore = useHotelDetailsPanelStore();
const hotelDetailsModalsStore = useHotelDetailsModalsStore();
const hotelDetailsMapStore = useHotelDetailsMapStore();
const statsigEventsStore = useStatsigEventsStore();
const hotelDetailsGalleryStore = useHotelDetailsGalleryStore();
const previouslyUserSessionStore = usePreviouslyUserSessionStore();
const mapStore = useMapStore();

const stays = ref<InstanceType<typeof HotelDetailsStaysSection>>();

const isRoomsGridLayoutExperiment = computedAsync<boolean>(
  () => isExperimentAsync('hotel_details_room_cards_grid_layout'),
  false
);

// NOTE: Part of data required to create HotelDetailsData interface is loaded in async manner,
// after HotelDetails.vue is rendered to the screen. Hence we are updating inner state after component creation.
watch(
  () => [props.hotelStaticData, props.hotelDynamicData],
  () => {
    if (
      props.hotelStaticData &&
      !isEqual(props.hotelStaticData, hotelDetailsPanelStore.hotelStaticData)
    ) {
      hotelDetailsPanelStore.setHotelStaticData(props.hotelStaticData);
    }

    if (props.hotelDynamicData) {
      hotelDetailsPanelStore.setHotelDynamicData(props.hotelDynamicData);
    }
    // Needed to initial render
    if (props.hotelStaticData && props.hotelStaticData.scrollToAvailability) {
      scrollToStays();
    }
  },
  { immediate: true }
);

function handleStaySelected(stayId: StayId): void {
  if (props.hotelStaticData === undefined) {
    return;
  }
  emit('stay-selected', {
    ingredientId: props.hotelStaticData.ingredientId,
    hotelCode: props.hotelStaticData.hotelCode,
    stayId,
  });
}

function handleToggleHeart(): void {
  if (props.hotelStaticData === undefined) {
    return;
  }
  emit('toggle-heart', {
    ingredientId: props.hotelStaticData.ingredientId,
    hotelCode: props.hotelStaticData.hotelCode,
    isPreviouslySelected: props.hotelStaticData.isHearted,
  });
}

function handleCloseFromKeyboard(): void {
  const hasModal =
    hotelDetailsModalsStore.hotelDetailsModalType !==
    HotelDetailsModalType.None;
  const hasGallery =
    hotelDetailsGalleryStore.currentOpenedGallery !== undefined;
  if (hasGallery || hasModal) {
    return;
  }
  emit('close');
}

function handleScrollToStays(isDefaultSource = true): void {
  scrollToStays();
  if (isDefaultSource) {
    statsigEventsStore.logHotelDetailsShowStaysButtonClicked({
      source: props.source,
    });
  } else {
    statsigEventsStore.logHotelDetailsShowStaysButtonClicked({
      source: HotelDetailsEventSource.HotelDetailsSelectionBox,
    });
  }
}

function handleContinueWithRoom(): void {
  emit('close');
  statsigEventsStore.logHotelDetailsContinueWithSelectedRooms({
    source: HotelDetailsEventSource.HotelDetailsSelectionBox,
  });
}

function scrollToStays(): void {
  nextTick(() => {
    const element = document.getElementById('hotel-details-stays');
    element?.scrollIntoView({ behavior: 'smooth' });
  });
}

function showSingleImageGallery(imageIndex: number): void {
  hotelDetailsGalleryStore.openFullScreenSingleImageGallery(
    {
      index: imageIndex,
      images: props.hotelStaticData?.staticInfo.images || [],
    },
    HotelDetailsGalleryType.SingleImageForHotel
  );
}

const isLoading = computed(
  () =>
    !props.hotelStaticData ||
    !props.hotelDynamicData ||
    isFeatureToggleActive('loader')
);

onMounted(async () => {
  statsigEventsStore.logHotelDetailsInitiated({ source: props.source });
  // When the hotel details panel is mounted, we prevent the body from scrolling.
  // This prevents the body scroll from appearing when the panel is open.
  document.body.style.overflow = 'hidden';
  if (hotelDetailsPanelStore.hotelStaticData?.hotelCode) {
    previouslyUserSessionStore.updatePreviouslySeenHotels(
      hotelDetailsPanelStore.hotelStaticData.hotelCode
    );
  }
  await nextTick();
  if (props.hotelStaticData) {
    trackHotelDetailsViewItemForECommerce(
      props.hotelStaticData.staticInfo.mainPolygon.id,
      props.hotelStaticData.staticInfo.typeOfHotel,
      props.hotelStaticData.hotelCode
    );
  }
});

onUnmounted(async () => {
  hotelDetailsGalleryStore.resetGalleryState();
  hotelDetailsModalsStore.closeModal();
  document.body.style.overflow = '';
});
</script>

<template>
  <AppFirstLevelPanel
    class="hotel-details-panel"
    @close="handleCloseFromKeyboard"
  >
    <template #content>
      <div class="content-wrapper">
        <HotelDetailsGallerySectionLoader
          v-if="isLoading"
          @close="emit('close')"
        />
        <HotelDetailsGallerySection
          v-else-if="props.hotelStaticData?.staticInfo.images"
          :gallery-type="hotelDetailsPanelStore.galleryType"
          :hotel-images="props.hotelStaticData?.staticInfo.images"
          @close="emit('close')"
          @show-full-gallery="
            hotelDetailsGalleryStore.openHotelDetailsFullscreenMasonryGallery(
              props.hotelStaticData?.staticInfo.images || []
            )
          "
          @show-single-image="showSingleImageGallery"
          @toggle-heart="handleToggleHeart"
        />

        <HotelDetailsDescriptionSectionLoader v-if="isLoading" />
        <HotelDetailsDescriptionSection
          v-else-if="hotelDetailsPanelStore.headerData"
          :header-data="hotelDetailsPanelStore.headerData"
          :source="props.source"
          :is-hearted="props.hotelStaticData?.isHearted || false"
          :is-availability-loading="
            props.hotelStaticData?.isAvailabilityLoading || false
          "
          :stay="hotelDetailsPanelStore.selectionSectionStay"
          @scroll-to-rooms="handleScrollToStays(false)"
          @continue-with-room="handleContinueWithRoom"
          @select-hotel="handleToggleHeart"
          @unselect-hotel="handleToggleHeart"
          @suggest-another-day="emit('suggest-another-day')"
        />

        <div
          v-if="
            props.hotelStaticData?.isSingleHotelRecipe &&
            props.hotelStaticData?.isHearted
          "
          class="ribbon-wrapper"
        >
          <FirstDayTripPriceRibbon
            :show-month-select-dropdown="false"
            :source="CoreEventSource.HotelDetails"
          />
        </div>

        <div v-if="isRoomsGridLayoutExperiment" class="divider" />

        <HotelStayFilters
          v-if="props.hotelStaticData"
          :options="props.hotelMealFilterOptions"
          @meal-option-selected="emit('meal-option-selected', $event)"
        />

        <HotelMealFiltersEmptyState
          v-if="
            !props.hotelStaticData?.isAvailabilityLoading &&
            !(
              hotelDetailsPanelStore.selectedStay ||
              hotelDetailsPanelStore.alternativeStays.length
            )
          "
          :options="props.hotelMealFilterOptions"
          @meal-option-removed="emit('meal-option-selected', $event)"
        />

        <template
          v-if="
            props.hotelDynamicData &&
            props.hotelStaticData &&
            !props.hotelStaticData.isAvailabilityLoading
          "
        >
          <GridLayoutHotelDetailsStaysSection
            v-if="isRoomsGridLayoutExperiment"
            ref="stays"
            :selected-stay="hotelDetailsPanelStore.selectedStay"
            :alternative-stays="hotelDetailsPanelStore.alternativeStays"
            :source="props.source"
            @stay-selected="handleStaySelected"
            @more-information="hotelDetailsModalsStore.openMoreInformatioModal"
            @open-gallery="
              hotelDetailsGalleryStore.openFullScreenSingleImageGallery(
                $event,
                HotelDetailsGalleryType.SingleImageForRoom
              )
            "
          />

          <HotelDetailsStaysSection
            v-else
            ref="stays"
            :selected-stay="hotelDetailsPanelStore.selectedStay"
            :alternative-stays="hotelDetailsPanelStore.alternativeStays"
            :ingredient-id="props.hotelStaticData.ingredientId"
            :source="props.source"
            @stay-selected="handleStaySelected"
            @more-information="hotelDetailsModalsStore.openMoreInformatioModal"
            @open-gallery="
              hotelDetailsGalleryStore.openFullScreenSingleImageGallery(
                $event,
                HotelDetailsGalleryType.SingleImageForRoom
              )
            "
          />
        </template>

        <template v-if="props.hotelStaticData?.isAvailabilityLoading">
          <ListRoomLoader />
        </template>
        <DestinationInformationSection
          v-if="props.hotelStaticData?.destinationInformation"
          :destination-information="
            props.hotelStaticData.destinationInformation
          "
        />
      </div>
    </template>
  </AppFirstLevelPanel>

  <DynamicFullScreenMap
    v-if="hotelDetailsMapStore.shouldShowFullscreenMap"
    :map-configuration="{
      markers: hotelDetailsMapStore.mapMarker,
      markerFormatters: hotelDetailsMapStore.getMapMarkerFormatters(
        MapType.Dynamic,
        mapStore.mapTypeId
      ),
      startRectangle: hotelDetailsMapStore.initialMapView,
    }"
    @close="hotelDetailsMapStore.closeDynamicMapModal"
  />
  <HotelDetailsGallery
    v-if="
      hotelDetailsGalleryStore.currentOpenedGallery &&
      hotelDetailsPanelStore.galleryData
    "
    :config="hotelDetailsPanelStore.galleryData"
    @close="hotelDetailsGalleryStore.closeHotelDetailsGallery"
    @image-clicked="
      hotelDetailsGalleryStore.openFullScreenSingleImageGallery(
        $event,
        HotelDetailsGalleryType.SingleImageForHotel
      )
    "
  />
  <HotelDetailsModals />
</template>

<style lang="scss" scoped>
.hotel-details-panel {
  background-color: var(--color-white);

  .content-wrapper {
    @include for-more-or-equal-than-1200-px {
      padding: var(--spacing-24) var(--spacing-24) var(--spacing-32)
        var(--spacing-24);
    }

    .divider {
      margin: 0 var(--spacing-16) var(--spacing-16) var(--spacing-16);
      border-top: 1px solid var(--color-scrollbar);

      @include for-more-or-equal-than-1200-px {
        margin: 0 0 var(--spacing-32) 0;
      }
    }
    .ribbon-wrapper {
      padding-top: var(--spacing-16);
      border-top: 1px solid var(--color-scrollbar);
    }
  }
}
::-webkit-scrollbar-track {
  background: var(--color-white);
}
</style>
